import { MoreHorizRounded } from "@mui/icons-material";
import {
  Box,
  Stack,
  Checkbox,
  Avatar,
  Typography,
  Chip,
  Button,
} from "@mui/material";
import { useState } from "react";

export interface Comment {
  id: number;
  sender_full_name: string | null;
  sender_email: string | null;
  content: string;
  created_at: string;
  is_approved: boolean;
  is_spam: boolean;
  comment_source: "generic" | "youtube";
}

interface CommentCardProps {
  comment: Comment;
  checked: boolean;
  onCheck: () => void;
  isLast?: boolean;
  onApprove: (isApproved: boolean) => void;
  onSpam: () => void;
}

export function CommentCard({
  comment,
  checked,
  onCheck,
  isLast = false,
  onApprove,
  onSpam,
}: CommentCardProps) {
  const [expanded, setExpanded] = useState(false);
  const TRUNCATE_LENGTH = 200;
  const isLong = comment.content.length > TRUNCATE_LENGTH;
  const displayContent =
    !expanded && isLong
      ? comment.content.slice(0, TRUNCATE_LENGTH) + "..."
      : comment.content;

  const formatDate = (d: string) =>
    new Date(d).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const sourceLabel =
    comment.comment_source === "youtube" ? "Youtube" : "Memorial Page";
  const sourceColor =
    comment.comment_source === "youtube" ? "#ef4444" : "#724e41";
  const sourceBg = comment.comment_source === "youtube" ? "#fef2f2" : "#fdf2ee";

  const initials = (comment.sender_full_name ?? "A")
    .split(" ")
    .map((n) => n.charAt(0))
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const avatarColor =
    comment.comment_source === "youtube" ? "#ef4444" : "#724e41";

  return (
    <Box sx={{ py: 1.5, position: "relative" }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        {/* Checkbox */}
        <Checkbox
          checked={checked}
          onChange={onCheck}
          size="medium"
          sx={{
            p: 0,
            mt: 0.5,
            color: "#64748B",
            "&.Mui-checked": { color: "#724e41" },
          }}
        />

        {!isLast && (
          <Box
            sx={{
              position: "absolute",
              left: 38,
              top: 56,
              bottom: -4,
              width: "2px",
              bgcolor: "#e2e8f0",
            }}
          />
        )}
        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: avatarColor,
            fontSize: "13px",
            fontWeight: 700,
            zIndex: 1,
          }}
        >
          {initials}
        </Avatar>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box>
              <Typography
                sx={{ fontWeight: 700, fontSize: "14px", color: "#1e293b" }}
              >
                {comment.sender_full_name ?? "Anonymous"}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" mt={0.3}>
                <Chip
                  label={sourceLabel}
                  size="small"
                  sx={{
                    bgcolor: sourceBg,
                    color: sourceColor,
                    fontWeight: 600,
                    fontSize: "11px",
                    height: 20,
                    borderRadius: "4px",
                  }}
                />
                <Typography sx={{ fontSize: "11px", color: "#1e293b" }}>
                  {formatDate(comment.created_at)}
                </Typography>
              </Stack>
            </Box>
            <MoreHorizRounded
              sx={{ fontSize: 32, color: "#000000", cursor: "pointer", pr: 2 }}
            />
          </Stack>

          {/* Body */}
          <Typography
            sx={{
              fontSize: "13px",
              color: "#000000",
              lineHeight: 1.6,
              mt: 1.5,
              mb: 1.5,
            }}
          >
            {displayContent}
            {isLong && (
              <Box
                component="span"
                onClick={() => setExpanded(!expanded)}
                sx={{
                  color: "#724e41",
                  fontWeight: 600,
                  cursor: "pointer",
                  ml: 0.5,
                }}
              >
                {expanded ? " see less" : "see more"}
              </Box>
            )}
          </Typography>

          {/* Actions */}
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              onClick={() => onApprove(comment.is_approved)}
              sx={{
                bgcolor: comment.is_approved ? "#dcfce7" : "#16a34a",
                color: comment.is_approved ? "#16a34a" : "#fff",
                borderRadius: "6px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "12px",
                px: 1.5,
                py: 0.5,
                minWidth: "unset",
                "&:hover": { bgcolor: "#15803d", color: "#fff" },
              }}
            >
              {comment.is_approved ? "Unapprove" : "Approve"}
            </Button>
            <Button
              size="small"
              onClick={onSpam}
              sx={{
                bgcolor: comment.is_spam ? "#fef2f2" : "#ef4444",
                color: comment.is_spam ? "#ef4444" : "#fff",
                borderRadius: "6px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "12px",
                px: 1.5,
                py: 0.5,
                minWidth: "unset",
                "&:hover": { bgcolor: "#dc2626", color: "#fff" },
              }}
            >
              Spam
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
